import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { DISCIPLINES, projectDisciplines, disciplinePaths } from '../disciplines';
import logo from '../assets/VilanoLogo.png';
import '../App.css';

const Sidebar = () => {
  const navigate        = useNavigate();
  const { logout }      = useAuth();
  const { projectId }   = useParams();
  const { pathname }    = useLocation();
  const [disciplines, setDisciplines] = useState(['electrical']);

  // Disciplines on this project (live, so adding one in Edit Project shows at once)
  useEffect(() => {
    if (!projectId) return undefined;
    return onSnapshot(doc(db, 'projects', projectId),
      snap => setDisciplines(projectDisciplines(snap.data())),
      () => setDisciplines(['electrical']));
  }, [projectId]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div style={styles.sidebar}>
      <div style={styles.logoArea}>
        <img src={logo} alt="Vilano Logo" style={styles.logo} />
      </div>

      <nav style={styles.nav}>
        {/* ── Global nav ── */}
        <button
          style={{ ...styles.navButton, ...styles.globalButton }}
          onClick={() => navigate('/dashboard')}
        >
          Dashboard
        </button>
        <button
          style={{ ...styles.navButton, ...styles.globalButton }}
          onClick={() => navigate('/projects')}
        >
          Projects
        </button>
        <button
          style={{ ...styles.navButton, ...styles.globalButton }}
          onClick={() => navigate('/settings')}
        >
          Settings
        </button>
        <button
          style={{ ...styles.navButton, ...styles.newProjectButton }}
          onClick={() => navigate('/project/new')}
        >
          + New Project
        </button>

        {/* ── Project-level nav: one group per discipline ── */}
        {projectId && (
          <>
            <div style={styles.divider} />
            {DISCIPLINES.filter(d => disciplines.includes(d.key)).map(d => {
              const paths = disciplinePaths(projectId, d.key);
              const item = (label, to) => (
                <button
                  key={to}
                  style={{ ...styles.navButton, ...styles.projectButton, ...(pathname === to ? styles.activeButton : {}) }}
                  onClick={() => navigate(to)}
                >
                  {label}
                </button>
              );
              return (
                <div key={d.key} style={{ marginBottom: '0.5rem' }}>
                  <div style={styles.sectionLabel}>{d.label}</div>
                  {item('Project Details', paths.details)}
                  {item('Report Editor', paths.photos)}
                  {item('Generate Report', paths.report)}
                </div>
              );
            })}
          </>
        )}
      </nav>

      <div style={styles.bottom}>
        <button
          style={{ ...styles.navButton, ...styles.signOutButton }}
          onClick={handleLogout}
        >
          Sign Out
        </button>
      </div>
    </div>
  );
};

const styles = {
  sidebar: {
    width: '200px',
    height: '100vh',
    position: 'fixed',
    top: 0,
    left: 0,
    backgroundColor: '#000000',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 10,
    boxSizing: 'border-box',
  },
  logoArea: {
    padding: '1rem',
    borderBottom: '1px solid #333',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logo: {
    width: '140px',
    height: 'auto',
    objectFit: 'contain',
  },
  nav: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    paddingTop: '0.75rem',
    overflowY: 'auto',
  },
  navButton: {
    width: '90%',
    margin: '4px auto',
    display: 'block',
    padding: '10px',
    fontSize: '0.95rem',
    borderRadius: '5px',
    cursor: 'pointer',
    border: 'none',
    textAlign: 'center',
    fontFamily: 'inherit',
  },
  globalButton: {
    backgroundColor: '#6495ED',
    color: 'white',
  },
  newProjectButton: {
    backgroundColor: '#f5a623',
    color: 'black',
    fontWeight: 'bold',
  },
  projectButton: {
    backgroundColor: 'rgba(100, 149, 237, 0.15)',
    color: '#a8bfed',
    fontSize: '0.88rem',
    padding: '8px 10px',
    border: '1px solid rgba(100, 149, 237, 0.3)',
  },
  activeButton: {
    backgroundColor: 'rgba(100, 149, 237, 0.4)',
    color: 'white',
  },
  signOutButton: {
    backgroundColor: 'rgb(229, 9, 20)',
    color: 'white',
  },
  divider: {
    height: '1px',
    backgroundColor: '#333',
    margin: '0.6rem auto',
    width: '85%',
  },
  sectionLabel: {
    color: 'rgba(100, 149, 237, 0.6)',
    fontSize: '0.7rem',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    padding: '0 0 0.3rem 1rem',
  },
  bottom: {
    paddingBottom: '1.5rem',
  },
};

export default Sidebar;